/**
 * V1.5 Sidebar — Brand + Steering + Workspace + Footer.
 * Quelle: Implementation Brief §7.10 + Screen-Inventory §1.3.
 *
 * Menü-Einträge pro Rolle:
 *   - Mission Control / Atlas / Phase Board / Staffing / Briefings — alle authed
 *   - Admin — users.is_admin === true ODER Funktionsrolle kuse_admin (F2, beschnittener
 *     Admin-Bereich — Filterung der Tabs passiert in `pages/admin.tsx` via `admin-gating.ts`)
 *   - Inbox + Audit-Trail-Vollansicht: Out-of-Scope V1 (Brief §12)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { canSeeAdminArea } from '../../lib/admin-gating.js';
import { useFeatureFlag } from '../../lib/features.js';
import { personaLabel, usePersona } from '../../lib/persona.js';
import { useSession } from '../../lib/session.js';

const SIDEBAR_COLLAPSE_KEY = 'pv:sidebar-collapsed';

type NavItem = {
  to: string;
  labelKey: string;
  fallback: string;
  icon: ReactIcon;
  end?: boolean;
};

type ReactIcon = (props: { className?: string }) => JSX.Element;

const HomeGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12l9-9 9 9" />
    <path d="M5 10v10h14V10" />
  </svg>
);

const GridGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const FlowGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="5" cy="6" r="2" />
    <circle cx="19" cy="6" r="2" />
    <circle cx="12" cy="18" r="2" />
    <path d="M7 6h10" />
    <path d="M5 8c0 4 3 7 7 8" />
    <path d="M19 8c0 4-3 7-7 8" />
  </svg>
);

const HandoverGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8h13" />
    <path d="M13 4l4 4-4 4" />
    <path d="M21 16H8" />
    <path d="M11 20l-4-4 4-4" />
  </svg>
);

const UsersGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M15 14c2.5 0 6 1.5 6 5" />
  </svg>
);

const SparkGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
  </svg>
);

const SettingsGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

const ServerGlyph: ReactIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="7" rx="1.5" />
    <rect x="3" y="13" width="18" height="7" rx="1.5" />
    <path d="M7 7.5h.01" />
    <path d="M7 16.5h.01" />
  </svg>
);

const STEERING_ITEMS: NavItem[] = [
  {
    to: '/',
    labelKey: 'v2.nav.mission_control',
    fallback: 'Mission Control',
    icon: HomeGlyph,
    end: true,
  },
  { to: '/atlas', labelKey: 'v2.nav.atlas', fallback: 'Portfolio Atlas', icon: GridGlyph },
  { to: '/board', labelKey: 'v2.nav.board', fallback: 'Phase Board', icon: FlowGlyph },
  { to: '/handover', labelKey: 'v2.nav.handover', fallback: 'Übergaben', icon: HandoverGlyph },
  // Die KL-zentrierte Sicht heißt seit PV-016 „Staffing" (/staffing); sichtbar wenn Flag
  // `staffing_neu` an ist (die alte Sicht /staffing-alt ist mit #163 entfallen).
  // Für die Funktionsrolle `kaufm_leitung` tritt die KFML-Worklist an ihre Stelle — siehe
  // unten in Sidebar(). Gesperrt ist nichts, /staffing bleibt für alle erreichbar.
  { to: '/staffing', labelKey: 'v2.nav.staffing', fallback: 'Staffing', icon: UsersGlyph },
];

// Einstieg der Kaufm. Leitung anstelle von „Staffing" (PV-016). Die Staffing-Ansicht ist von
// dort aus über den Tab „Staffing" erreichbar — identische Komponente, nichts geht verloren.
const KFML_WORKLIST_ITEM: NavItem = {
  to: '/kfml-worklist',
  labelKey: 'v2.nav.kfml_worklist',
  fallback: 'KFML-Worklist',
  icon: UsersGlyph,
};

const WORKSPACE_ITEMS: NavItem[] = [
  { to: '/briefings', labelKey: 'v2.nav.briefings', fallback: 'Briefings', icon: SparkGlyph },
  {
    to: '/einstellungen',
    labelKey: 'v2.nav.settings',
    fallback: 'Einstellungen',
    icon: SettingsGlyph,
  },
];

export function Sidebar() {
  const { t } = useTranslation();
  const { user } = useSession();
  // F2: Admin-Eintrag für is_admin ODER KuSe-Admins (kein is_admin, aber Funktionsrolle
  // kuse_admin) — sie sehen dort einen beschnittenen Bereich (nur Benutzer/Rollen + Tags).
  const showAdminEntry = user ? canSeeAdminArea(user) : false;
  const showBriefings = useFeatureFlag('briefings_library');
  const showStaffingNeu = useFeatureFlag('staffing_neu');
  const workspaceItems = WORKSPACE_ITEMS.filter(
    (item) => item.to !== '/briefings' || showBriefings,
  );
  // Der Nav-Eintrag hängt an der FUNKTIONSROLLE, nicht an der Persona: die Persona ist eine
  // umschaltbare Sicht (users.preferences.active_persona) — hinge die Navigation daran, würde
  // der Menüpunkt beim Umschalten des Dropdowns erscheinen und verschwinden. Der Einstieg ist
  // eine Eigenschaft des Users, nicht seiner gerade gewählten Brille.
  const isKfml = (user?.function_roles ?? []).some((fr) => fr.role === 'kaufm_leitung');
  const steeringItems = STEERING_ITEMS
    // KL: „Staffing" wird durch „KFML-Worklist" ERSETZT (nicht ergänzt) — zwei Einträge, die
    // dieselbe Sicht öffnen, wären Rauschen.
    .map((item) => (item.to === '/staffing' && isKfml ? KFML_WORKLIST_ITEM : item))
    // Das Flag gilt für BEIDE: die Worklist enthält die Staffing-Ansicht, sie darf nicht
    // sichtbar sein, solange die es nicht ist.
    .filter((item) => (item.to !== '/staffing' && item.to !== '/kfml-worklist') || showStaffingNeu);

  const [collapsed, setCollapsed] = useState<boolean>(
    () => window.localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1',
  );
  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'flex items-center rounded-md text-sm font-medium transition-colors',
      collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2',
      isActive
        ? collapsed
          ? 'bg-[var(--surface-2)] text-[color:var(--ink)]'
          : 'bg-[var(--surface-2)] text-[color:var(--ink)] border-l-2 border-l-[var(--primary)] -ml-[2px] pl-[calc(0.75rem-2px+2px)]'
        : 'text-[color:var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[color:var(--ink)]',
    ].join(' ');

  const renderItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end ?? false}
      className={linkClass}
      title={collapsed ? t(item.labelKey, item.fallback) : undefined}
    >
      <item.icon className="shrink-0" />
      {!collapsed && <span>{t(item.labelKey, item.fallback)}</span>}
    </NavLink>
  );

  return (
    <aside
      className="flex flex-col shrink-0 border-r sticky top-0 overflow-hidden"
      style={{
        width: collapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
        height: '100vh',
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
        transition: 'width var(--motion-base) var(--easing)',
      }}
    >
      {/* Brand — eingeklappt nur das Logo */}
      <div
        className={`flex items-center border-b ${collapsed ? 'px-2 py-4 justify-center' : 'px-4 py-4 gap-2.5'}`}
        style={{ borderColor: 'var(--line-soft)' }}
      >
        <img
          src="/ms_logo.png"
          alt="mindsquare"
          className="shrink-0"
          style={collapsed ? { maxWidth: '100%', height: 'auto' } : { height: 22, width: 'auto' }}
        />
        {!collapsed && (
          <span
            className="font-semibold uppercase text-[10px] truncate min-w-0"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)', letterSpacing: '0.08em' }}
          >
            {t('app.title', 'Projektverwaltung')}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 overflow-y-auto py-4 space-y-6 ${collapsed ? 'px-2' : 'px-3'}`}>
        <SidebarSection label={t('v2.nav.steering', 'Steering')} collapsed={collapsed}>
          {steeringItems.map(renderItem)}
        </SidebarSection>

        <SidebarSection label={t('v2.nav.workspace', 'Workspace')} collapsed={collapsed}>
          {workspaceItems.map(renderItem)}
          {showAdminEntry && (
            <NavLink
              to="/admin"
              className={linkClass}
              title={collapsed ? t('v2.nav.admin', 'Admin') : undefined}
            >
              <ServerGlyph className="shrink-0" />
              {!collapsed && <span>{t('v2.nav.admin', 'Admin')}</span>}
            </NavLink>
          )}
        </SidebarSection>
      </nav>

      {/* Footer: Persona-Toggle + Sync-Pill (eingeklappt nur Collapse-Button) */}
      <div
        className={`border-t space-y-2 py-3 ${collapsed ? 'px-2' : 'px-3'}`}
        style={{ borderColor: 'var(--line-soft)' }}
      >
        {!collapsed && <PersonaToggle />}
        {!collapsed && <SyncPill />}
        <CollapseToggle collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      </div>
    </aside>
  );
}

function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      title={collapsed ? t('v2.nav.expand', 'Ausklappen') : t('v2.nav.collapse', 'Einklappen')}
      className={`w-full flex items-center rounded-md text-xs transition-colors ${collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-3 py-1.5'}`}
      style={{
        background: 'var(--surface-2)',
        color: 'var(--ink-2)',
        border: '1px solid var(--line-soft)',
        fontFamily: 'var(--f-mono)',
      }}
    >
      <span>{collapsed ? '»' : '«'}</span>
      {!collapsed && <span>{t('v2.nav.collapse', 'Einklappen')}</span>}
    </button>
  );
}

function SidebarSection({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        className={`mb-2 font-semibold uppercase ${collapsed ? 'text-center text-[9px] tracking-[0.06em]' : 'px-3 text-[10px] tracking-widest'}`}
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {label}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function PersonaToggle() {
  const { t } = useTranslation();
  const { available, active, setActive, isPending, isLoading } = usePersona();
  if (isLoading) return null;
  if (available.length <= 1) {
    return (
      <div
        className="px-3 py-1.5 rounded-md text-[11px] flex items-center justify-between"
        style={{
          background: 'var(--surface-2)',
          color: 'var(--ink-3)',
          fontFamily: 'var(--f-mono)',
        }}
      >
        <span style={{ color: 'var(--ink-3)' }}>{t('v2.sidebar.persona_single', 'Sicht')}</span>
        <span style={{ color: 'var(--ink-2)' }}>{personaLabel(active)}</span>
      </div>
    );
  }
  return (
    <label className="block">
      <span
        className="block text-[10px] uppercase tracking-[0.14em] mb-1 px-1"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {t('v2.sidebar.persona_label', 'Aktive Sicht')}
      </span>
      <select
        value={active}
        onChange={(e) => setActive(e.target.value as never)}
        disabled={isPending}
        className="w-full text-xs px-2 py-1.5 rounded-md outline-none"
        style={{
          background: 'var(--surface-2)',
          color: 'var(--ink)',
          border: '1px solid var(--line-soft)',
          fontFamily: 'var(--f-mono)',
        }}
      >
        {available.map((p) => (
          <option key={p} value={p}>
            {personaLabel(p)}
          </option>
        ))}
      </select>
    </label>
  );
}

function SyncPill() {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs"
      style={{
        background: 'var(--surface-2)',
        color: 'var(--ink-2)',
        fontFamily: 'var(--f-mono)',
      }}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{
          background: 'var(--success)',
          animation: 'pulse-soft 2s ease-in-out infinite',
        }}
      />
      <span>{t('v2.sidebar.sync_live', 'Sync · live')}</span>
    </div>
  );
}
